/**
 * usePipelineStages — Fetch, update, and reorder pipeline stage configurations.
 * Blueprint: F-10 — pipeline_stages table replaces fixed ENUM.
 *
 * Default 7 stages seeded per tort: INTAKE → DOC_COLLECTION → REVIEW →
 *   SUBSTANTIATED (allows packet gen) → AWARD_READY → DEFICIENT → CLOSED (terminal).
 *
 * System Admin can: rename labels, reorder. Cannot delete if active_claimant_count > 0.
 */
import { useState, useCallback } from 'react';
import type { PipelineStageConfig, TortCode } from '@/types/cmm/claimant.types';

interface UsePipelineStagesReturn {
  stages:      PipelineStageConfig[];
  isLoading:   boolean;
  error:       string | null;
  fetchStages:  (tortCode: TortCode) => Promise<void>;
  updateStage:  (stageId: string, updates: Partial<PipelineStageConfig>) => Promise<void>;
  reorderStages:(tortCode: TortCode, orderedIds: string[]) => Promise<void>;
  deleteStage:  (stageId: string) => Promise<void>;
}

export function usePipelineStages(): UsePipelineStagesReturn {
  const [stages,    setStages]    = useState<PipelineStageConfig[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error,     setError]     = useState<string | null>(null);

  const fetchStages = useCallback(async (tortCode: TortCode) => {
    setIsLoading(true);
    try {
      // TODO: GET /admin/pipeline-stages/:tort_code
      //   Returns stages ordered by sort_order for PipelineBoard column rendering
      setStages([]);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateStage = useCallback(async (stageId: string, updates: Partial<PipelineStageConfig>) => {
    // TODO: PUT /admin/pipeline-stages/:tort_code — rename label, toggle allows_packet_generation
    console.warn('[usePipelineStages] updateStage — TODO:', stageId, updates);
  }, []);

  const reorderStages = useCallback(async (tortCode: TortCode, orderedIds: string[]) => {
    // TODO: PUT /admin/pipeline-stages/:tort_code with new sort_order values
    console.warn('[usePipelineStages] reorderStages — TODO:', tortCode, orderedIds);
  }, []);

  const deleteStage = useCallback(async (stageId: string) => {
    // TODO: DELETE /admin/pipeline-stages/:stage_id
    //   Server returns 409 if active_claimant_count > 0
    console.warn('[usePipelineStages] deleteStage — TODO:', stageId);
  }, []);

  return { stages, isLoading, error, fetchStages, updateStage, reorderStages, deleteStage };
}
